import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { clampPopoverLeft } from '../../Common/popover';

import { ReactComponent as MailIcon } from '@repo/common/assets/icons/16px/icon_16px_mail_outline.svg';
import { ReactComponent as XCircleIcon } from '@repo/common/assets/icons/16px/icon_16px_x-circle_outline.svg';
import { ReactComponent as ClockPlusIcon } from '@repo/common/assets/icons/16px/icon_16px_clock-plus_outline.svg';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/16px/icon_16px_check.svg';

import { BaseButton, useT } from '@repo/common';

const ITEM_BG = 'bg-grayscale-gray00 border border-grayscale-gray00';
const ITEM_ACTIVE = 'bg-grayscale-gray02 text-text-primary';

/**
 * 어드민 결제 대기 행의 옵션 메뉴.
 *
 * 카드(Eximbay) 인보이스: 결제 요청 재발송 · 결제 취소 · (기한 연장 — 미구현)
 * 계좌이체 인보이스(paymentProvider === 'BANK_TRANSFER'): 위에 "이체 확인" 이 먼저 붙고,
 * 결제 요청 재발송은 입금 계좌 안내 재발송으로 읽힌다. 3시간이 지나 EXPIRED 된 행은 확인만 가능.
 */
function OptionList({
  anchorRect,
  onClose,
  status,
  paymentProvider,
  // 결제 레코드가 아직 없는 HOLD(유저가 Pay now 를 누르기 전)면 이체 확인·안내 재발송은 할 수 없다
  hasPayment = true,
  onSendPaymentRequest,
  onCanclePayment,
  onConfirmBankTransfer,
}) {
  const t = useT();
  const listRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);

  if (!anchorRect) return null;

  const isBankTransfer = String(paymentProvider || '').toUpperCase() === 'BANK_TRANSFER';
  const isExpired = String(status || '').toLowerCase() === 'expired';
  const canActOnBankTransfer = isBankTransfer && hasPayment;

  const handleItemClick = async (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveItem(action);

    if (action === 'confirm') {
      onConfirmBankTransfer?.();
      onClose?.();
      return;
    }
    if (action === 'send') {
      onSendPaymentRequest?.();
      onClose?.();
      return;
    }
    if (action === 'cancel') {
      onCanclePayment?.();
      onClose?.();
      return;
    }
    onClose?.();
  };

  const itemProps = (action, { disabled = false } = {}) => ({
    type: 'text',
    size: 'm',
    iconPosition: 'after',
    width: '100%',
    separate: true,
    leftalign: true,
    onClick: (e) => handleItemClick(e, action),
    bgclass: `${ITEM_BG} ${activeItem === action ? ITEM_ACTIVE : ''}`,
    textclass: 'typo-b14Strong',
    hoverclass: disabled ? '' : 'hover:bg-grayscale-gray01 hover:text-text-primary',
    activeclass: disabled ? '' : 'active:bg-grayscale-gray02 active:text-text-primary',
    isDisabled: disabled,
  });

  return (
    <>
      {anchorRect &&
        ReactDOM.createPortal(
          <>
            <div
              className='fixed inset-0 z-[999999] bg-transparent'
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
            />

            <div
              ref={listRef}
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                top: anchorRect.bottom + 6,
                left: clampPopoverLeft(anchorRect.right - 24, 240),
              }}
              className={`
                w-[240px]
                bg-grayscale-gray00
                border border-grayscale-gray02
                rounded-lg
                shadow-popup-04
                z-[1000000]
              `}
            >
              <div className='bg-grayscale-gray00 rounded-lg p-2'>
                <ul className='flex flex-col'>
                  {canActOnBankTransfer && (
                    <li>
                      <BaseButton {...itemProps('confirm')} icon={<CheckIcon />}>
                        {isExpired
                          ? t('Confirm late transfer', '늦은 입금 확인')
                          : t('Confirm bank transfer', '이체 확인')}
                      </BaseButton>
                    </li>
                  )}

                  {isBankTransfer && !hasPayment && (
                    <li className='px-3 py-2'>
                      <p className='typo-b12 text-text-placeholder'>
                        {t(
                          'Guest has not confirmed the bank transfer step yet.',
                          '게스트가 아직 계좌이체 단계를 진행하지 않았습니다.',
                        )}
                      </p>
                    </li>
                  )}

                  {!isExpired && (!isBankTransfer || hasPayment) && (
                    <li>
                      <BaseButton {...itemProps('send')} icon={<MailIcon />}>
                        {isBankTransfer
                          ? t('Resend transfer details', '입금 안내 재발송')
                          : t('Send payment request', '결제 요청 보내기')}
                      </BaseButton>
                    </li>
                  )}

                  {!isExpired && (
                    <li>
                      <BaseButton {...itemProps('cancel')} icon={<XCircleIcon />}>
                        {t('Cancel payment', '결제 취소')}
                      </BaseButton>
                    </li>
                  )}

                  {!isExpired && (
                    <li>
                      <BaseButton
                        {...itemProps('extend', { disabled: true })}
                        icon={<ClockPlusIcon />}
                      >
                        {t('Extend payment deadline', '결제 기한 연장')}
                      </BaseButton>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
export default OptionList;
